// src/components/documents/CatalogPickerPanel.js — doced-8f3a61c2 (Document editor, Stage 4)
//
// "Add from catalog": a panel from the right. Search, folders (a folder includes its sub-folders —
// the server does that), Favourites; tick items, set how many, "Add N items". Ticks survive changing
// the search or the folder, so items from several folders can be added in one go.
// Cost and markup are shown only when the server sent them (Owner / Admin / Manager).
// The page turns the ticked items into lines (catalogPickLines.appendPickedEstimateLines), which
// also removes an untouched empty line at the bottom.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, Search, Loader2, Star, AlertCircle, Minus, Plus } from 'lucide-react';
import { usePanelCatalogSearch } from './useCatalogSearch';
import { customerUnit } from '../../utils/catalogPickLines';
import { formatCurrency } from '../../utils/formatters';
import { formatPct } from '../../utils/catalogUi';

const FAV = '__fav';

export default function CatalogPickerPanel({ open, initialQuery = '', onClose, onAdd, folders, currency }) {
    const [query, setQuery] = useState(initialQuery);
    const [chip, setChip] = useState(''); // '' all | FAV | folder id
    const [sel, setSel] = useState(() => new Map()); // id → { item, qty }
    const searchRef = useRef(null);
    const openerRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        openerRef.current = document.activeElement;
        setQuery(initialQuery);
        setChip('');
        setSel(new Map());
        const t = setTimeout(() => searchRef.current && searchRef.current.focus(), 0);
        const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
        document.addEventListener('keydown', onKey);
        return () => {
            clearTimeout(t);
            document.removeEventListener('keydown', onKey);
            const o = openerRef.current;
            if (o && typeof o.focus === 'function' && document.contains(o)) o.focus();
        };
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const filters = { query, folderId: chip && chip !== FAV ? chip : '', favoritesOnly: chip === FAV };
    const search = usePanelCatalogSearch(filters, { enabled: open });

    // Top-level folders as chips; once one is chosen, its sub-folders as a second row.
    const topFolders = folders.tree.children(null);
    const chosen = chip && chip !== FAV ? folders.tree.byId.get(chip) : null;
    const parentOfChosen = chosen ? (chosen.parentId && folders.tree.byId.has(chosen.parentId) ? chosen.parentId : chosen.id) : null;
    const subFolders = parentOfChosen ? folders.tree.children(parentOfChosen) : [];

    const setQty = (item, n) => setSel((prev) => {
        const next = new Map(prev);
        if (n <= 0) next.delete(item.id); else next.set(item.id, { item, qty: n });
        return next;
    });
    const picks = useMemo(() => [...sel.values()], [sel]);
    const pickedTotal = picks.reduce((s, p) => s + (Number(p.item.price) || 0) * p.qty, 0);
    const n = picks.length;

    if (!open) return null;

    const chipCls = (on) => `shrink-0 min-h-[40px] px-3 rounded-full text-sm font-semibold whitespace-nowrap border ${on ? 'bg-[#5F4EC9] text-white border-[#5F4EC9]' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`;

    return (
        <div className="fixed inset-0 z-[70]">
            <div className="absolute inset-0 bg-gray-900/30" onClick={onClose} aria-hidden="true" />
            <aside role="dialog" aria-modal="true" aria-labelledby="doced-panel-title"
                className="absolute top-0 right-0 bottom-0 w-full max-w-[500px] bg-white shadow-2xl flex flex-col">
                <div className="p-4 flex flex-col gap-3 border-b border-gray-100">
                    <div className="flex justify-between items-center">
                        <h2 id="doced-panel-title" className="text-lg font-bold text-gray-900">Add from catalog</h2>
                        <button type="button" aria-label="Close" onClick={onClose} className="w-11 h-11 inline-flex items-center justify-center border border-gray-200 rounded-lg hover:bg-gray-50"><X size={20} /></button>
                    </div>
                    <label htmlFor="doced-panel-search" className="sr-only">Search the catalog</label>
                    <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                        <input id="doced-panel-search" ref={searchRef} type="search" value={query} onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search name, part #, manufacturer…" autoComplete="off"
                            className="w-full min-h-[44px] pl-9 pr-3 text-[15px] border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/40" />
                    </div>
                    <div role="group" aria-label="Folders" className="flex gap-2 overflow-x-auto pb-1">
                        <button type="button" aria-pressed={chip === ''} className={chipCls(chip === '')} onClick={() => setChip('')}>All</button>
                        <button type="button" aria-pressed={chip === FAV} className={chipCls(chip === FAV)} onClick={() => setChip(FAV)}>
                            <span className="inline-flex items-center gap-1"><Star size={14} aria-hidden="true" />Favourites</span>
                        </button>
                        {topFolders.map((f) => (
                            <button key={f.id} type="button" aria-pressed={parentOfChosen === f.id} className={chipCls(parentOfChosen === f.id)} onClick={() => setChip(f.id)}>{f.name}</button>
                        ))}
                    </div>
                    {subFolders.length > 0 && (
                        <div role="group" aria-label="Sub-folders" className="flex gap-2 overflow-x-auto pb-1">
                            <button type="button" aria-pressed={chip === parentOfChosen} className={chipCls(chip === parentOfChosen)} onClick={() => setChip(parentOfChosen)}>All of {folders.tree.byId.get(parentOfChosen)?.name}</button>
                            {subFolders.map((f) => (
                                <button key={f.id} type="button" aria-pressed={chip === f.id} className={chipCls(chip === f.id)} onClick={() => setChip(f.id)}>{f.name}</button>
                            ))}
                        </div>
                    )}
                    {folders.error && <p className="text-xs text-amber-700">{folders.error} You can still search by name.</p>}
                </div>

                <div className="flex-1 overflow-y-auto p-2" aria-busy={search.status === 'loading'}>
                    {search.status === 'loading' && (
                        <div className="flex items-center justify-center gap-2 py-10 text-gray-500 text-sm"><Loader2 size={18} className="animate-spin" aria-hidden="true" />Searching the catalog…</div>
                    )}
                    {search.status === 'error' && (
                        <div className="m-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2">
                            <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden="true" />
                            <div className="flex-1">{search.error}</div>
                            <button type="button" onClick={search.retry} className="min-h-[36px] px-3 rounded-md border border-red-300 bg-white font-semibold">Try again</button>
                        </div>
                    )}
                    {(search.status === 'done' || search.status === 'loadingMore') && search.items.length === 0 && (
                        <p className="py-10 text-center text-sm text-gray-500">
                            {chip === FAV && !query.trim() ? 'No favourites yet. Star items on the Catalog screen to see them here.' : 'Nothing in the catalog matches. Try other words or another folder.'}
                        </p>
                    )}
                    {search.items.map((it) => {
                        const picked = sel.get(it.id);
                        const qty = picked ? picked.qty : 0;
                        const cid = `doced-pick-${it.id}`;
                        const unit = customerUnit(it);
                        const sub = [folders.pathOf(it), unit].filter(Boolean).join(' · ');
                        const hasCost = Object.prototype.hasOwnProperty.call(it, 'cost') && it.cost !== null && it.cost !== undefined;
                        return (
                            <div key={it.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg min-h-[56px] ${qty ? 'bg-[#5F4EC9]/10' : 'bg-white hover:bg-gray-50'}`}>
                                <input id={cid} type="checkbox" checked={qty > 0} onChange={() => setQty(it, qty ? 0 : 1)}
                                    className="w-5 h-5 shrink-0 accent-[#5F4EC9] cursor-pointer" />
                                <label htmlFor={cid} className="flex-1 min-w-0 cursor-pointer py-1.5">
                                    <span className="block font-semibold text-sm text-gray-900 truncate">{it.name}</span>
                                    {sub && <span className="block text-xs text-gray-500 truncate">{sub}</span>}
                                </label>
                                <div className="text-right shrink-0">
                                    <div className="font-bold text-sm tabular-nums">{formatCurrency(Number(it.price) || 0, currency)}</div>
                                    {hasCost && <div className="text-xs text-gray-500 tabular-nums">cost {formatCurrency(Number(it.cost) || 0, currency)}{it.markupPct !== null && it.markupPct !== undefined ? ` · ${formatPct(it.markupPct)}` : ''}</div>}
                                </div>
                                {qty > 0 && (
                                    <div className="flex items-center gap-1 shrink-0">
                                        <button type="button" aria-label={`Fewer ${it.name}`} onClick={() => setQty(it, qty - 1)} className="w-10 h-10 inline-flex items-center justify-center border border-gray-300 rounded-lg bg-white"><Minus size={16} /></button>
                                        <span className="min-w-[24px] text-center font-bold tabular-nums" aria-live="polite">{qty}</span>
                                        <button type="button" aria-label={`More ${it.name}`} onClick={() => setQty(it, qty + 1)} className="w-10 h-10 inline-flex items-center justify-center border border-gray-300 rounded-lg bg-white"><Plus size={16} /></button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {search.hasMore && search.items.length > 0 && (
                        <div className="p-2 flex justify-center">
                            <button type="button" onClick={search.loadMore} disabled={search.status === 'loadingMore'}
                                className="min-h-[44px] px-4 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-60">
                                {search.status === 'loadingMore' ? 'Loading…' : 'Show more'}
                            </button>
                        </div>
                    )}
                    {search.error && search.status === 'done' && search.items.length > 0 && (
                        <p className="px-3 pb-2 text-xs text-red-600">{search.error}</p>
                    )}
                </div>

                <div className="border-t border-gray-100 p-4 flex flex-wrap justify-between items-center gap-3">
                    <span className="text-sm text-gray-700" aria-live="polite">
                        {n ? `${n} ${n === 1 ? 'item' : 'items'} ticked · ${formatCurrency(pickedTotal, currency)}` : 'Tick items to add them'}
                    </span>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="min-h-[44px] px-4 border border-gray-300 rounded-lg bg-white font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
                        <button type="button" disabled={!n} onClick={() => onAdd(picks)}
                            className="min-h-[44px] px-5 rounded-lg font-bold bg-[#5F4EC9] text-white hover:bg-[#4f40b0] disabled:bg-[#5F4EC9]/30 disabled:cursor-not-allowed">
                            {n ? `Add ${n} ${n === 1 ? 'item' : 'items'}` : 'Add items'}
                        </button>
                    </div>
                </div>
            </aside>
        </div>
    );
}
